import Link from 'next/link'
import { ChangeEvent, MouseEvent, TouchEvent } from 'react'

import { Slider } from 'src/components/atoms/Slider'

export type SliderInputCardRebrandProps = {
  label: string
  value: number
  unitPrefix?: string
  unitSuffix?: string
  min: number
  max: number
  step: number
  onChange: (next: number) => void
  // Fired when the numeric input loses focus / the slider is released, with the
  // raw field value. Used to restore the legacy `*_calculator_input` /
  // `*_calculator_slider` analytics; optional so non-analytics call sites can
  // omit them.
  onInputBlur?: (value: number) => void
  onSliderRelease?: (value: number) => void
  minLabel: string
  maxLabel: string
  helpLinkLabel?: string
  helpLinkHref?: string
  // Optional outer container override. Defaults to the calculator section's
  // sizing; other call sites (e.g. /get-started) can pass their own.
  containerClassName?: string
}

const DEFAULT_CONTAINER_CLASSES =
  'flex w-full flex-col justify-between gap-4 py-2 lg:w-[525px] lg:pr-12'

export const SliderInputCardRebrand = ({
  label,
  value,
  unitPrefix,
  unitSuffix,
  min,
  max,
  step,
  onChange,
  onInputBlur,
  onSliderRelease,
  minLabel,
  maxLabel,
  helpLinkLabel,
  helpLinkHref,
  containerClassName = DEFAULT_CONTAINER_CLASSES
}: SliderInputCardRebrandProps) => {
  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const raw = Number(e.currentTarget.value)
    if (Number.isNaN(raw)) return
    if (raw > max) return onChange(max)
    onChange(raw)
  }

  const handleInputBlur = (e: ChangeEvent<HTMLInputElement>) => {
    const raw = Number(e.currentTarget.value)
    if (Number.isNaN(raw) || raw < min) onChange(min)
    onInputBlur?.(raw)
  }

  const handleSliderChange = (e: ChangeEvent<HTMLInputElement>) => {
    onChange(Number(e.currentTarget.value) || min)
  }

  const handleSliderRelease = (
    e: MouseEvent<HTMLInputElement> | TouchEvent<HTMLInputElement>
  ) => {
    onSliderRelease?.(Number(e.currentTarget.value))
  }

  return (
    <div className={containerClassName}>
      <div className='flex w-full flex-col gap-[5px]'>
        <p className='font-body-rebrand text-forestDeep text-[16px] leading-[1.6]'>
          {label}
        </p>
        <label
          htmlFor={`slider-input-${label}`}
          className='border-espresso/20 flex h-[70px] w-full items-center overflow-hidden rounded-2xl border px-5'
        >
          {unitPrefix && (
            <span className='font-body-rebrand text-espresso/50 mr-3 text-[32px] leading-[1.2] font-normal'>
              {unitPrefix}
            </span>
          )}
          <input
            id={`slider-input-${label}`}
            type='number'
            min={min}
            max={max}
            step={step}
            value={Number(value).toString()}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            className='number-no-scroll font-body-rebrand text-forestDeep w-full bg-transparent text-[32px] leading-[1.2] font-extrabold outline-none'
          />
          {unitSuffix && (
            <span className='font-body-rebrand text-espresso/50 ml-2 text-[32px] leading-[1.2] font-normal'>
              {unitSuffix}
            </span>
          )}
        </label>
        <div className='relative h-[26px] w-full'>
          <Slider
            className='text-espresso'
            value={value}
            min={min}
            max={max}
            step={step}
            onChange={handleSliderChange}
            onMouseUp={handleSliderRelease}
            onTouchEnd={handleSliderRelease}
          />
        </div>
        <div className='font-body-rebrand text-forestDeep flex w-full justify-between text-[12px] leading-[1.6]'>
          <span>{minLabel}</span>
          <span>{maxLabel}</span>
        </div>
      </div>
      {helpLinkLabel && helpLinkHref && (
        <Link
          href={helpLinkHref}
          target='_blank'
          rel='noreferrer'
          className='font-body-rebrand text-forestDeep text-[12px] leading-[1.6] underline underline-offset-2 hover:opacity-80'
        >
          {helpLinkLabel}
        </Link>
      )}
    </div>
  )
}

export default SliderInputCardRebrand
